import { CircularProgress, Typography } from "@material-ui/core"
import PropTypes from "prop-types"
import { useContext, useEffect, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useHistory } from "react-router-dom"
import NoData from "~/components/Common/NoData"
import CourseCard from "~/components/courses/CourseCard"
import AppContext from "~/contexts/App"
import sortHelper from "~/utils/sortHelper"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const CourseCardList = ({
  searchValue,
  sort,
  openCreateDialog,
  statusState,
  setStatusNumber,
  filteredCoursesByType,
  coursesLength,
}) => {
  const [filteredData, setFilteredData] = useState([])
  const [filteredAmount, setFilteredAmount] = useState({})
  const classes = useStyles()
  const history = useHistory()
  const { setPageSubTitle } = useContext(AppContext)
  //amount of data that we show using the infinite scroll
  const [showedData, setShowedData] = useState([])

  //set draft, published, unpublished, archived length
  useEffect(() => {
    setStatusNumber({
      published: filteredCoursesByType.filter((x) => x.published === 1).length,
      unpublished: filteredCoursesByType.filter(
        (x) =>
          x.published === 0 && x.has_been_published === 1 && x.archived === 0,
      ).length,
      draft: filteredCoursesByType.filter(
        (x) =>
          x.published === 0 &&
          x.has_been_published === 0 &&
          x.archived === 0 &&
          !x.publish_date,
      ).length,
      archived: filteredCoursesByType.filter(
        (x) => x.archived === 1 && x.published === 0,
      ).length,
      scheduled: filteredCoursesByType.filter(
        (x) =>
          x.publish_date &&
          x.published === 0 &&
          x.archived === 0 &&
          x.has_been_published === 0,
      ).length,
    })
  }, [filteredCoursesByType])

  //filteres depending on the searchValue or sort
  useEffect(() => {
    if (filteredCoursesByType) {
      let newArray = [...filteredCoursesByType]

      if (searchValue.length > 0 || sort?.name !== "") {
        if (searchValue.length > 0) {
          newArray = newArray?.filter((x) => {
            return (
              x.name.toLowerCase().includes(searchValue.toLowerCase()) ||
              x.container_id === Number(searchValue)
            )
          })
        }
        if (sort?.name !== "") {
          //util function to sort the data
          newArray = sortHelper(
            newArray,
            sort?.type,
            sort?.name,
            sort?.direction,
          )
        } else {
          newArray = sortHelper(newArray, "date", "last updated", "DSC")
        }
      } else {
        newArray = sortHelper(newArray, "date", "last updated", "DSC")
      }

      if (!newArray) {
        setFilteredAmount({
          published: 0,
          unpublished: 0,
          draft: 0,
          archived: 0,
          scheduled: 0,
        })
      } else {
        setFilteredAmount({
          published: newArray.filter((x) => x.published === 1).length,
          unpublished: newArray.filter(
            (x) =>
              x.published === 0 &&
              x.has_been_published === 1 &&
              x.archived === 0,
          ).length,
          draft: newArray.filter(
            (x) =>
              x.published === 0 &&
              x.has_been_published === 0 &&
              x.archived === 0 &&
              !x.publish_date,
          ).length,
          archived: newArray.filter(
            (x) => x.archived === 1 && x.published === 0,
          ).length,
          scheduled: newArray.filter(
            (x) =>
              x.published === 0 &&
              x.has_been_published === 0 &&
              x.archived === 0 &&
              x.publish_date,
          ).length,
        })
        if (statusState === "published") {
          newArray = newArray.filter((x) => x.published === 1)
        } else if (statusState === "unpublished") {
          newArray = newArray.filter(
            (x) =>
              x.published === 0 &&
              x.has_been_published === 1 &&
              x.archived === 0,
          )
        } else if (statusState === "draft") {
          newArray = newArray.filter(
            (x) =>
              x.published === 0 &&
              x.has_been_published === 0 &&
              x.archived === 0 &&
              !x.publish_date,
          )
        } else if (statusState === "archived") {
          newArray = newArray.filter(
            (x) => x.archived === 1 && x.published === 0,
          )
        } else if (statusState === "scheduled") {
          newArray = newArray.filter(
            (x) =>
              x.published === 0 &&
              x.has_been_published === 0 &&
              x.archived === 0 &&
              x.publish_date,
          )
        }
      }

      setFilteredData(newArray)
      //show 20 first elements of the newArray
      setShowedData(newArray?.slice(0, 40 || []))
    }
  }, [searchValue, sort, filteredCoursesByType, statusState])

  useEffect(() => {
    setPageSubTitle(filteredAmount[statusState])
  }, [filteredCoursesByType, filteredAmount])

  //resets the length based on search
  useEffect(() => {
    setStatusNumber({
      published: filteredAmount.published,
      unpublished: filteredAmount.unpublished,
      draft: filteredAmount.draft,
      archived: filteredAmount.archived,
      scheduled: filteredAmount.scheduled,
    })
  }, [searchValue, filteredData])

  //loadData using infinite scroll
  const loadData = () => {
    setShowedData(() => filteredData.slice(0, showedData.length + 20))
  }

  //send the user to the top if a status state, sort or search value change
  useEffect(() => {
    const element = document.getElementById("coursesListScroll")
    if (element) {
      element.scrollTo(0, 0)
    }
  }, [statusState, sort, searchValue])
  return (
    <Box className={classes.container} id="coursesListScroll">
      {filteredData?.length > 0 ? (
        <InfiniteScroll
          style={{ overflow: "hidden" }}
          dataLength={showedData?.length || 0}
          scrollableTarget="coursesListScroll"
          next={loadData}
          loader={
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          }
          hasMore={filteredData?.length > showedData?.length || false}
          endMessage={
            <Box width="100%" textAlign="center">
              {filteredData.length > 20 && (
                <Typography className={classes.postsInfiniteText}>
                  End of Courses List
                </Typography>
              )}
            </Box>
          }
        >
          <Box display="flex" flexWrap="wrap">
            {showedData?.map((course) => {
              return (
                <CourseCard course={course} key={course.id} lessonLength="" />
              )
            })}
          </Box>
        </InfiniteScroll>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
          minHeight="80vh"
        >
          <NoData
            text="You have not created any courses"
            buttonFunc={openCreateDialog}
            searchValue={searchValue}
            buttonText="create my first course"
            length={
              filteredCoursesByType?.length > 0
                ? filteredCoursesByType?.length
                : coursesLength
            }
            searchLength={filteredData?.length || 0}
          />
        </Box>
      )}
    </Box>
  )
}

CourseCardList.propTypes = {
  searchValue: PropTypes.string,
  sort: PropTypes.object,
  openCreateDialog: PropTypes.func,
  statusState: PropTypes.string,
  setStatusNumber: PropTypes.func,
  filteredCoursesByType: PropTypes.array,
  coursesLength: PropTypes.number,
}

export default CourseCardList
